import React from "react";
import { Divider, Tooltip, Typography } from "@mui/material";
import {
  DataTable,
  TableBody,
  TableCell,
  TableCellRow,
  TableHead,
  TableHeader,
  TableHeadRow,
} from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { contactName, fullName } from "utils/fullName";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  FormikInvoice,
  PaymentTermsLabels,
} from "utils/invoices/invoiceFormat";
import ContactType from "enums/ContactType";
import { titleCase } from "title-case";
import TransactionsType from "enums/TransactionsType";
import {
  RenderOption,
  RenderTransactionType,
} from "components/common/RenderTransactionType";
import {
  invoiceDisplayStatus,
  invoiceStatusLabelKey,
} from "utils/invoices/invoiceStatus";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { SummaryItem } from "components/common/Summary";
import type { TFunction } from "i18next";

const FIGURE_SX = { fontVariantNumeric: "tabular-nums" };

const TotalsRow: React.FC<{ label: string; figure: string }> = ({
  label,
  figure,
}) => (
  <Inline wrap={false} justify="between" gap="var(--space-3)">
    <Typography variant="body1" sx={{ color: "var(--color-text-secondary)" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={FIGURE_SX}>
      {figure}
    </Typography>
  </Inline>
);

type LineItem = {
  id: string | number;
  productOrService: string;
  amount: number;
  quantity: number;
  total: number;
};

interface LineItemsTableProps {
  details: LineItem[];
  currency: string;
}

export const LineItemsTable: React.FC<LineItemsTableProps> = ({
  details,
  currency,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ marginBottom: "var(--space-3)", overflowX: "auto" }}>
      <DataTable>
        <TableHeader>
          <TableHeadRow>
            <TableHead>{t("Product/Service")}</TableHead>
            <TableHead align="right">{t("Amount")}</TableHead>
            <TableHead align="right">{t("Quantity")}</TableHead>
            <TableHead align="right">{t("Total")}</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {details.map((detail) => (
            <TableCellRow key={detail.id}>
              <TableCell maxWidth={160}>
                <Tooltip title={detail.productOrService}>
                  <span>{detail.productOrService}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                {formatCashFull(detail.amount, currency)}
              </TableCell>
              <TableCell align="right">{detail.quantity}</TableCell>
              <TableCell align="right">
                {formatCashFull(detail.total, currency)}
              </TableCell>
            </TableCellRow>
          ))}
        </TableBody>
      </DataTable>
    </Box>
  );
};

export interface InvoiceHistoryRow {
  type: string;
  date: number;
  displayName?: string;
  comment?: string;
}

export const buildInvoiceHistoryRows = (
  invoice: FormikInvoice,
  t: TFunction,
): InvoiceHistoryRow[] => {
  const normalizeDate = (date: unknown): number => {
    if (!date) return 0;
    if (typeof date === "number") return date;
    if (typeof date === "string" && /^\d+$/.test(date)) return Number(date);
    if (date instanceof Date) return date.getTime();
    return 0;
  };

  const rows: InvoiceHistoryRow[] = [];

  if (invoice.createdAt) {
    rows.push({
      type: t("Created"),
      date: normalizeDate(invoice.createdAt),
      displayName: fullName(invoice.submittedBy) || undefined,
    });
  }

  const sentTypeLabels: Record<string, string> = {
    ESTIMATE: t("Estimate Sent"),
    PAYMENT_FAILED_NOTICE: t("Payment Failed Notice Sent"),
  };
  for (const h of invoice.sentHistory || []) {
    rows.push({
      type: sentTypeLabels[h.sentType ?? ""] || t("Invoice Sent"),
      date: normalizeDate(h.sentDate),
      displayName: fullName(h.sentBy) || undefined,
    });
  }

  for (const h of invoice.estimateActionHistory || []) {
    rows.push({
      type:
        h.action === "ESTIMATE_APPROVED"
          ? t("Estimate Approved")
          : t("Estimate Rejected"),
      date: normalizeDate(h.timestamp),
      displayName: fullName(h.updatedBy) || h.updatedBy?.businessName,
      comment: h.comment,
    });
  }

  if (invoice.paidDate) {
    rows.push({
      type: t("Paid"),
      date: normalizeDate(invoice.paidDate),
      displayName: invoice.paidBy?.name,
    });
  }

  if (invoice.canceledDate) {
    rows.push({
      type: t("Canceled"),
      date: normalizeDate(invoice.canceledDate),
      displayName: fullName(invoice.canceledBy) || undefined,
    });
  }

  return rows.sort((a, b) => b.date - a.date);
};

interface InvoiceHistoryTableProps {
  rows: InvoiceHistoryRow[];
}

export const InvoiceHistoryTable: React.FC<InvoiceHistoryTableProps> = ({
  rows,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        marginBottom: "var(--space-3)",
        maxHeight: 320,
        overflowY: "auto",
      }}
    >
      <DataTable>
        <TableHeader>
          <TableHeadRow>
            <TableHead>{t("Event")}</TableHead>
            <TableHead>{t("By")}</TableHead>
            <TableHead>{t("Date/Time")}</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => (
            <TableCellRow key={idx}>
              <TableCell>
                {row?.comment ? (
                  <Tooltip title={row.comment}>
                    <span>{row?.type}</span>
                  </Tooltip>
                ) : (
                  row?.type
                )}
              </TableCell>
              <TableCell>{row?.displayName}</TableCell>
              <TableCell>
                {row?.date ? (
                  <Tooltip
                    title={moment(row.date).format("MM/DD/YYYY hh:mm:ss A")}
                  >
                    <span>{moment(row.date).format("MM/DD/YYYY")}</span>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </TableCell>
            </TableCellRow>
          ))}
        </TableBody>
      </DataTable>
    </Box>
  );
};

interface InvoiceTotalDetailsProps {
  invoice: FormikInvoice;
}

export const InvoiceTotalDetails: React.FC<InvoiceTotalDetailsProps> = ({
  invoice,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="var(--space-3)">
      <TotalsRow
        label={t("Subtotal")}
        figure={formatCashFull(invoice?.subtotal, invoice?.currency)}
      />
      {invoice?.discountAmount !== 0 && (
        <TotalsRow
          label={t("Discount")}
          figure={formatCashFull(invoice?.discountAmount, invoice?.currency)}
        />
      )}
      {invoice?.tax !== 0 && (
        <TotalsRow
          label={t("Tax")}
          figure={formatCashFull(invoice?.taxAmount, invoice?.currency)}
        />
      )}
      {invoice?.serviceFeeAmount !== 0 && (
        <TotalsRow
          label={t("Service Fee")}
          figure={formatCashFull(invoice?.serviceFeeAmount, invoice?.currency)}
        />
      )}
      <Divider />
      <Inline wrap={false} justify="between" gap="var(--space-3)">
        <Typography variant="h5">{t("Total")}</Typography>
        <Typography variant="h5" sx={FIGURE_SX}>
          {formatCashFull(invoice?.total, invoice?.currency)}
        </Typography>
      </Inline>
    </Stack>
  );
};

export function buildInvoiceSummaryItems({
  invoice,
  setOpenContactDetailsDrawer,
  setTransactionReviewDrawerId,
}: {
  invoice: any;
  setOpenContactDetailsDrawer: (id: string) => void;
  setTransactionReviewDrawerId: (id: string) => void;
}): SummaryItem[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation();

  return [
    {
      title: t("Invoice Number"),
      value: invoice?.invoiceNumber,
    },
    {
      title: t("Invoice Name"),
      value: invoice?.invoiceName,
    },
    {
      title: t("PO Number"),
      value: invoice?.purchaseOrderId,
    },
    {
      title: t("Recipient"),
      value: contactName(invoice?.primaryRecipient) || null,
      onClick: () => setOpenContactDetailsDrawer(invoice?.primaryRecipient?.id),
    },
    {
      title: t("Due Date"),
      value: invoice?.dueDate
        ? moment(invoice?.dueDate).format("MM/DD/YYYY")
        : PaymentTermsLabels[
            invoice.paymentTerms as keyof typeof PaymentTermsLabels
          ],
    },
    {
      title: t("Paid Date"),
      value:
        invoice?.paidDate &&
        invoice?.dueDate &&
        moment(invoice.paidDate).isAfter(moment(invoice.dueDate), "day") ? (
          <Typography
            component="span"
            variant="subtitle2"
            sx={{ color: "var(--color-text-danger)" }}
          >
            {moment(invoice.paidDate).format("MM/DD/YYYY")}
          </Typography>
        ) : invoice?.paidDate ? (
          moment(invoice.paidDate).format("MM/DD/YYYY")
        ) : null,
    },
    {
      title: t("Paid By"),
      value: invoice?.paidBy?.name,
      onClick: () =>
        invoice?.paidBy && setOpenContactDetailsDrawer(invoice.paidBy.id),
    },
    {
      title: t("Processing Fee"),
      value: invoice?.linkedTransaction?.fees?.feeAmount
        ? formatCashFull(
            invoice.linkedTransaction.fees.feeAmount,
            invoice?.currency,
          )
        : null,
    },
    {
      title: t("Allocation Type"),
      value: invoice?.allocationType?.name,
    },
    {
      title: t("Allocation Name"),
      value: invoice?.allocationName?.allocationCode,
    },
    {
      title: t("Status"),
      value: t(
        invoiceStatusLabelKey(
          invoiceDisplayStatus({
            status: invoice?.status,
            disputeStatus: invoice?.disputeData?.disputeStatus,
            dueDate: invoice?.dueDate,
            microdepositPending: invoice?.microdepositPending,
          }),
        ),
      ),
    },
    {
      title: t("Failure Reason"),
      value: invoice?.paymentError?.message ? (
        <Typography
          component="span"
          variant="subtitle2"
          sx={{ color: "var(--color-text-danger)" }}
        >
          {invoice.paymentError.message}
        </Typography>
      ) : null,
    },
    {
      title: t("Linked Transaction"),
      value: invoice?.linkedTransaction?.id && (
        <RenderTransactionType
          transactionType={invoice?.linkedTransaction.type as TransactionsType}
          cardType={""}
          renderOption={RenderOption.TEXT}
        />
      ),
      onClick: () =>
        setTransactionReviewDrawerId(invoice?.linkedTransaction?.id || ""),
    },
    {
      title: t("Dispute Status"),
      value: titleCase(
        (invoice?.disputeData?.disputeStatus || "").toLowerCase(),
      ),
    },
    ...(invoice.filteredCustomFieldSummaryItems?.length > 0
      ? invoice.filteredCustomFieldSummaryItems
      : []),
    {
      title: t("CC Recipients"),
      value:
        invoice?.secondaryRecipients &&
        invoice.secondaryRecipients.length > 0 ? (
          <Stack gap="var(--space-1)">
            {invoice.secondaryRecipients.map((recipient: any) => (
              <MuiLink
                key={recipient.id}
                component="button"
                type="button"
                sx={{ textAlign: "left" }}
                onClick={() => setOpenContactDetailsDrawer(recipient.id)}
              >
                {contactName(recipient)}
              </MuiLink>
            ))}
          </Stack>
        ) : null,
      groupBy: "CC Recipients",
    },
  ];
}
